// Extra CMS slots for the 'privacy' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
//
// Sections 2 ("What we collect") and 3 ("What we don't do") already have their
// BODIES wired via the base registry slots privacy.what_we_collect (step_list)
// and privacy.what_we_dont_do (bullet_list); this file adds their eyebrow/title
// headings plus every other section's heading + prose, the hero, the draft
// banner, and the closing "wider frame" tiles.
//
// The "Who we are" and "Your rights" bodies contain an inline mailto anchor (and
// "Who we are" a bold legal-entity name). Per the shared rules, links stay as
// FIXED JSX (href hardcoded) — the editable prose is split into pre/post markdown
// slots around the fixed anchor, exactly as it renders today.
import type { SlotDef } from '../slots';

export const privacyExtra: SlotDef[] = [
  // ─── Hero ─────────────────────────────────────────────────────────────
  {
    key: 'privacy.hero_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Hero eyebrow label',
    helpText: 'The "Last updated" label; the date beside it is the separate "Last updated date" slot.',
    default: 'Last updated',
  },
  {
    key: 'privacy.last_updated',
    page: 'privacy',
    kind: 'markdown',
    label: 'Last updated date',
    helpText: 'Shown in the hero eyebrow after the label (e.g. "9 May 2026").',
    default: '9 May 2026',
  },
  {
    key: 'privacy.hero_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Privacy Policy.',
  },
  {
    key: 'privacy.hero_lede',
    page: 'privacy',
    kind: 'markdown',
    label: 'Hero lede',
    default: "What we collect, what we don't, and what you can ask us to do with it.",
  },

  // ─── Draft banner ─────────────────────────────────────────────────────
  {
    key: 'privacy.draft_banner_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Draft banner: title',
    default: 'Draft, under legal review',
  },
  {
    key: 'privacy.draft_banner_body',
    page: 'privacy',
    kind: 'markdown',
    label: 'Draft banner: body',
    default:
      'This is a placeholder Privacy Policy pending review by counsel. The final version will replace this page before WWAAVE launches publicly.',
  },

  // ─── Section headings (eyebrow / title) ───────────────────────────────
  {
    key: 'privacy.who_we_are_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who we are: eyebrow',
    default: 'Section 1',
  },
  {
    key: 'privacy.who_we_are_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who we are: title',
    default: 'Who we are.',
  },
  {
    key: 'privacy.what_we_collect_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'What we collect: eyebrow',
    default: 'Section 2',
  },
  {
    key: 'privacy.what_we_collect_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'What we collect: title',
    default: 'What we collect.',
  },
  {
    key: 'privacy.what_we_dont_do_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: "What we don't do: eyebrow",
    default: 'Section 3',
  },
  {
    key: 'privacy.what_we_dont_do_title',
    page: 'privacy',
    kind: 'markdown',
    label: "What we don't do: title",
    default: "What we don't do.",
  },
  {
    key: 'privacy.your_rights_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Your rights: eyebrow',
    default: 'Section 4',
  },
  {
    key: 'privacy.your_rights_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Your rights: title',
    default: 'Your rights.',
  },
  {
    key: 'privacy.cookies_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Cookies: eyebrow',
    default: 'Section 5',
  },
  {
    key: 'privacy.cookies_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Cookies: title',
    default: 'Cookies.',
  },
  {
    key: 'privacy.changes_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Changes to this policy: eyebrow',
    default: 'Section 6',
  },
  {
    key: 'privacy.changes_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Changes to this policy: title',
    default: 'Changes to this policy.',
  },

  // ─── Section bodies ───────────────────────────────────────────────────
  // "Who we are" wraps a bold legal-entity name and a fixed mailto anchor;
  // pre/entity/post are the three editable text runs around them.
  {
    key: 'privacy.who_we_are_pre',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who we are: text before company name',
    helpText: 'Reads: "<this> WWAAVE PTY LTD <post text> hello@wwaave.com."',
    default: 'WWAAVE is operated by',
  },
  {
    key: 'privacy.who_we_are_entity',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who we are: company name (bold)',
    default: 'WWAAVE PTY LTD',
  },
  {
    key: 'privacy.who_we_are_post',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who we are: text after company name',
    helpText: 'Runs up to the contact email (hello@wwaave.com), which is a fixed link.',
    default: '(ACN 689 639 423), a company registered in Australia. You can reach us at',
  },
  // "Your rights" has the mailto anchor mid-sentence; pre/post are the runs
  // either side of the fixed link.
  {
    key: 'privacy.your_rights_pre',
    page: 'privacy',
    kind: 'markdown',
    label: 'Your rights: text before contact email',
    helpText: 'Ends just before the hello@wwaave.com link.',
    default:
      'Under Australian Privacy Principles and (where applicable) GDPR, you can request access to the data we hold about you, correction of incorrect data, and deletion of your account. Contact us at',
  },
  {
    key: 'privacy.your_rights_post',
    page: 'privacy',
    kind: 'markdown',
    label: 'Your rights: text after contact email',
    default: 'to exercise any of these rights.',
  },
  {
    key: 'privacy.cookies_body',
    page: 'privacy',
    kind: 'markdown',
    label: 'Cookies: body',
    helpText:
      "Describes the cookies the build actually sets. Don't add a promise about consent banners or opt-in analytics unless one is built — WWAAVE sets no analytics cookies at all.",
    default:
      "We set four cookies, all our own: one to keep you signed in, one to protect forms from being submitted by another site on your behalf, one to remember whether you're browsing as an artist or a host, and, if you arrived through an artist's share link, one that remembers who sent you so they get the credit. We set no analytics cookies, no advertising cookies, and no third-party cookies of any kind. Because we don't set them, there's no cookie banner to click through.",
  },
  // "Who else touches your data" — the processors a visitor's data actually
  // reaches. A policy that lists only what WE do is incomplete: running the
  // platform necessarily hands data to a small set of suppliers, and naming
  // them is what makes the "we don't sell your data" promise checkable.
  // Keep this list honest against the build — if a supplier is added or
  // dropped, this section changes with it.
  {
    key: 'privacy.service_providers_eyebrow',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who else touches your data: eyebrow',
    default: 'Service providers',
  },
  {
    key: 'privacy.service_providers_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who else touches your data: title',
    default: 'Who else touches your data',
  },
  {
    key: 'privacy.service_providers_intro',
    page: 'privacy',
    kind: 'markdown',
    label: 'Who else touches your data: intro paragraph',
    default:
      'Running WWAAVE means a handful of suppliers handle some of your data on our behalf. Each one is listed here, with what they see. None of them is an advertiser, and none of them is permitted to use your data for their own purposes.',
  },
  {
    key: 'privacy.service_providers',
    page: 'privacy',
    kind: 'step_list',
    label: 'Who else touches your data: the list',
    helpText:
      'Title = the supplier, body = what they see. Must match the build — this is the section a regulator would check first.',
    default: [
      {
        title: 'Cloudflare',
        body: 'hosts the platform and serves every page. Like any web host, it sees the network requests your browser makes, including your IP address.',
      },
      {
        title: 'Wasabi',
        body: 'stores our off-platform nightly backups, in Sydney. Identity documents are deliberately excluded from those backups so that deleting one really does delete it.',
      },
      {
        title: 'Resend',
        body: 'delivers the emails we send you, so it handles your email address and the contents of those emails.',
      },
      {
        title: 'Brevo',
        body: "holds the waitlist. If you joined before launch, that's your first name, your email address, and whether you signed up as an artist or a venue.",
      },
      {
        title: 'Twilio',
        body: 'sends the SMS code that confirms your mobile number, so it handles that number.',
      },
      {
        title: 'Google Fonts and OpenStreetMap',
        body: "serve the typefaces the site is set in and the map tiles on the browse page. Loading either tells them your IP address, the same as visiting any site that uses them. Neither sets a cookie and neither is told who you are.",
      },
    ],
  },
  {
    key: 'privacy.changes_body',
    page: 'privacy',
    kind: 'markdown',
    label: 'Changes to this policy: body',
    default:
      "When we make material changes, we notify everyone signed in by email and post the previous version's archive link at the bottom of this page.",
  },
  {
    key: 'privacy.changes_contact',
    page: 'privacy',
    kind: 'markdown',
    label: 'Changes to this policy: contact line',
    helpText: 'The small dimmed line under the paragraph (legal entity, ACN, contact email).',
    default: 'WWAAVE PTY LTD · ACN 689 639 423 · hello@wwaave.com',
  },

  // ─── Closing "wider frame" tiles ──────────────────────────────────────
  {
    key: 'privacy.next_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame: block title',
    default: 'The wider frame.',
  },
  {
    key: 'privacy.next_lede',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame: block lede',
    default: 'The terms set out the broader rules; the promise explains the principle.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'privacy.next_terms_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame tile 1 (Terms): title',
    default: 'Terms of service',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'privacy.next_terms_desc',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame tile 1 (Terms): description',
    default: 'Eligibility, subscriptions, content, conduct, governing law.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'privacy.next_promise_title',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame tile 2 (Promise): title',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'privacy.next_promise_desc',
    page: 'privacy',
    kind: 'markdown',
    label: 'Wider frame tile 2 (Promise): description',
    default: 'No cuts, no paid placement, no data sales. What we will never do.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
];
